import React, { ReactElement, ReactNode, useContext, useState } from 'react';
import ModalContext from '../../components/Context/ModalContext';
import { useActionProps } from '../../utils/useActionProps';
import ConnectedFormContext from '../../components/Context/ConnectedFormContext';
import { IWorkspaceAction } from '../../@types/workspaceTypes';
import {
  IFilterInput,
  IRaiseSupplierReturnFromGoodsReceiptLinesMutationResult,
  IRaiseSupplierReturnFromGoodsReceiptLinesMutationVariables,
} from '../../graphql/schema/p4types';
import { useMutation } from '@apollo/client';
import { handleComponentQueryError } from '../../utils/parseErrorResponse';
import { useDispatch } from 'react-redux';
import { serviceActions } from '../../state/modules/service';
import getClientMutationId from '../../utils/getClientMutationId';
import normalizeVariables, { normalizeFields } from '../../graphql/schema/normalizeVariables';
import { SupplierReturn } from '../../graphql/entities';
import RaiseSupplierReturnFromGoodsReceiptLines from '../../graphql/mutations/product/RaiseSupplierReturnFromGoodsReceiptLines.graphql';
import SupplierReturnFormModal from '../../components/modals/SupplierReturnFormModal';

enum GoodsReceiptLineWorkspaceAction {
  SupplierReturnFromGoodsReceipt = 'SUPPLIER_RETURN_FROM_GOODS_RECEIPT',
}

export const workspaceActionsForPermissions = [
  {
    actionReference: GoodsReceiptLineWorkspaceAction.SupplierReturnFromGoodsReceipt,
  },
];

export const useSupplierReturnFromGoodsReceiptLinesContextAction: () => [
  (filters: Array<IFilterInput>, originLines: Array<any>, include: boolean) => void,
  ReactNode | ReactElement
] = () => {
  const { onClose } = useContext(ModalContext); //This will be 'onClose' from the 'Line Actions'
  const connectedForm = useContext(ConnectedFormContext);

  const [raiseReturnOrder] = useMutation<
    IRaiseSupplierReturnFromGoodsReceiptLinesMutationResult,
    IRaiseSupplierReturnFromGoodsReceiptLinesMutationVariables
  >(RaiseSupplierReturnFromGoodsReceiptLines, {
    onError: (data) =>
      handleComponentQueryError(data, { dispatch, entity: SupplierReturn, name: 'Could not raise return' }),
  });
  const dispatch = useDispatch();

  const [supplierReturnState, setSupplierReturnState] = useState<{ modalOpen: boolean; initialValues?: any }>({
    modalOpen: false,
  });

  const supplierReturnModal = (
    <SupplierReturnFormModal
      key={'supplierReturnModal'}
      modalState={supplierReturnState}
      closeModal={() => {
        //Reload form data, since the credit may have made changes to the order or order lines & hence changed the concurrency version (So a subsequent save may throw a conflict resolution error).
        connectedForm.reloadData?.(true);
        setSupplierReturnState({ modalOpen: false });
        onClose?.(); //Close the line workspace modal (This action is only displayed when in a modal from the purchase order form).
      }}
    />
  );

  const raiseReturnAction = (filters: Array<IFilterInput>, originLines: Array<any>, include: boolean) => {
    if (include && (!originLines || originLines.length === 0)) {
      dispatch(serviceActions.genericAlert('Invalid action', 'Must select one or more lines.'));
      return;
    }

    raiseReturnOrder({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          filters,
          originLines: normalizeVariables(originLines, [...normalizeFields, 'nettWeight', 'supplementaryUnits']),
          include,
        },
      },
    }).then((data) => {
      const supplierReturn = data?.data?.raiseSupplierReturnFromGoodsReceiptLines?.result;
      if (supplierReturn?.uuid) {
        setSupplierReturnState({ modalOpen: true, initialValues: supplierReturn });
      }
    });
  };

  return [raiseReturnAction, supplierReturnModal];
};

const useSupplierReturnFromGoodsReceiptLinesAction: () => [IWorkspaceAction, ReactNode | ReactElement] = () => {
  const { filters, originLines, include } = useActionProps('originLines');
  const [raiseReturnAction, supplierReturnModal] = useSupplierReturnFromGoodsReceiptLinesContextAction();

  const onClick = () => {
    raiseReturnAction(filters, originLines, include);
  };

  return [
    {
      key: 'raiseReturn',
      onClick: onClick,
      text: 'Raise Return',
      actionReference: GoodsReceiptLineWorkspaceAction.SupplierReturnFromGoodsReceipt,
    },
    supplierReturnModal,
  ];
};

export const useGoodsReceiptLineWorkspaceActions: () => [Array<IWorkspaceAction>, ReactNode | ReactElement] = () => {
  const [returnAction, supplierReturnModal] = useSupplierReturnFromGoodsReceiptLinesAction();

  return [[returnAction], supplierReturnModal];
};
